// Phase 14 — Affiliate Billing Badge (read-only view on affiliate dashboard)
import React, { useEffect, useState } from "react";
import { DollarSign, CheckCircle, AlertTriangle, Gift, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function AffiliateBillingBadge() {
  const [b, setB] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/affiliate/me/billing-status")
      .then((r) => setB(r.data))
      .catch(() => setB({ is_affiliate: false }))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div className="glass-panel p-3 flex items-center gap-2 text-xs text-lifted-dim" data-testid="aff-billing-badge-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading billing status…
      </div>
    );
  }
  if (!b?.is_affiliate) return null;

  // Phase 16 — Partner Affiliate is the user-facing label for any
  // free-access affiliate created via the LiftedAffiliate2025 code or the
  // UpPromote migration. Admins still see `payment_required: false` etc.
  const isPartnerAffiliate = b.access_type === "partner_affiliate"
                           || b.payment_status === "partner_affiliate";
  const isFree = !b.payment_required;
  const needsAttn = b.needs_attention;

  return (
    <div className="glass-panel p-4" data-testid="aff-billing-badge">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim uppercase mb-1">
            Affiliate access
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bebas tracking-[0.22em] text-sm text-white uppercase">
              {b.tier?.replace("_", " ") || "—"} tier
            </span>
            {isPartnerAffiliate ? (
              <span className="px-2 py-0.5 rounded-full border border-indigo-400/40 bg-indigo-400/10 text-indigo-200 text-[0.55rem] font-bebas tracking-[0.22em] uppercase inline-flex items-center gap-1"
                    data-testid="aff-badge-partner-affiliate">
                <Gift className="h-3 w-3" /> Partner Affiliate
              </span>
            ) : isFree ? (
              <span className="px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[0.55rem] font-bebas tracking-[0.22em] uppercase inline-flex items-center gap-1"
                    data-testid="aff-badge-free">
                <Gift className="h-3 w-3" /> Sponsored access
              </span>
            ) : needsAttn ? (
              <span className="px-2 py-0.5 rounded-full border border-rose-400/40 bg-rose-400/10 text-rose-300 text-[0.55rem] font-bebas tracking-[0.22em] uppercase inline-flex items-center gap-1"
                    data-testid="aff-badge-past-due">
                <AlertTriangle className="h-3 w-3" /> Past due
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full border border-lifted-green/40 bg-lifted-green/10 text-lifted-green text-[0.55rem] font-bebas tracking-[0.22em] uppercase inline-flex items-center gap-1"
                    data-testid="aff-badge-paid">
                <CheckCircle className="h-3 w-3" /> {b.payment_status?.replace("_", " ") || "active"}
              </span>
            )}
          </div>
          <div className="text-[0.7rem] text-lifted-dim space-y-0.5">
            <div>Access type: <span className="capitalize text-white/90">{(b.access_type || "—").replace("_", " ")}</span></div>
            {b.payment_required ? (
              <>
                <div>Monthly: <span className="text-white/90">${((b.monthly_price_cents || 0) / 100).toFixed(2)}</span></div>
                {b.next_charge_at && (
                  <div>Next charge: <span className="text-white/90">{(b.next_charge_at || "").slice(0, 10)}</span></div>
                )}
              </>
            ) : isPartnerAffiliate ? (
              <div>Your affiliate access is sponsored and approved as a Partner Affiliate. No monthly charge.</div>
            ) : (
              <div>Your affiliate access is sponsored. No monthly charge.</div>
            )}
          </div>
        </div>
        <DollarSign className={`h-5 w-5 ${isFree ? "text-amber-300" : "text-lifted-green"}`} />
      </div>
      {needsAttn && (
        <div className="mt-2 text-[0.65rem] text-rose-300" data-testid="aff-billing-attention">
          Your latest payment didn't go through. Contact admin or update your billing in your Stripe customer portal.
        </div>
      )}
    </div>
  );
}
